import _ from "lodash";
import {parse} from "date-fns";
import {getApi, logout, userInfo} from "~/auth";
import UI from "~/ui";

export const isNullish = (value : any) : boolean => {
    return typeof(value) === "undefined" || value === null || value === "";
}

export const coalesce = function<T> (value: T | null | undefined, defaultValue: T) : T {
    if (typeof(value) === "undefined" || value === null || value === "") {
        return defaultValue;
    }
    return value;
}

export const navigateTo = async (url: string) : Promise<void> => {
    UI.showWait();
    const auth = getApi();
    await auth.navigateTo(url);
}

export const returnUrl = (url?: string | null): string | undefined | null => {
    if (typeof url === "undefined") {
        // Leggi direttamente da sessionStorage invece di usare getCached
        return sessionStorage.getItem("urlRefer") || undefined;
    }

    if (url === "" || url === null) {
        // Rimuovi da sessionStorage
        sessionStorage.removeItem("urlRefer");
    } else {
        // Salva direttamente in sessionStorage
        sessionStorage.setItem("urlRefer", url);
    }
}

export const gotoLogin = async (redirectUrl?: string | null, message?:string): Promise<void> => {
    if (message && message.length > 0) {
        sessionStorage.setItem("loginMessage", message);
    }
    
    if ((await userInfo())?.isAuthenticated) {
        await logout(false);
    }
    
    let url = window.BaseUrlWeb + "Login";
    if (redirectUrl && redirectUrl.length > 0) {
        url = url + "?returnUrl=" + encodeURIComponent(redirectUrl);
    }
    
    await navigateTo(url);
}

export const gotoDefault = async (): Promise<void> => {
    let redirectUrl = returnUrl();
    if (redirectUrl) {
        returnUrl("");
        if (redirectUrl.length > 0) {
            // Verifica se è un URL assoluto esterno al sito
            const isAbsoluteUrl = /^(https?:\/\/|\/\/)/i.test(redirectUrl);
            const isExternalUrl = isAbsoluteUrl && !redirectUrl.startsWith(window.BaseUrlWeb);

            if (isExternalUrl) {
                // Se è un URL esterno, reindirizza alla home
                console.warn(`Tentativo di redirect a URL esterna bloccato: ${redirectUrl}`);
                await navigateTo(window.BaseUrlWeb);
                return;
            }

            // Gestione normale per URL relative o interne
            if (!redirectUrl.startsWith(window.BaseUrlWeb)) {
                redirectUrl = window.BaseUrlWeb + redirectUrl.replace(/^\//, '');
            }
            await navigateTo(redirectUrl);
        }
    } else {
        await navigateTo(window.BaseUrlWeb);
    }
}

export const gotoHome = async (redirectUrl?: string): Promise<void> => {
    if (redirectUrl) {
        if (redirectUrl.length > 0) {
            if (!redirectUrl.startsWith(window.BaseUrlWeb))
                localStorage.setItem("urlRefer", window.BaseUrlWeb + redirectUrl.replace(/^\//, ''));
        }
    }

    await navigateTo(window.BaseUrlWeb + "Home/Index");
}
export const formNavigateTo = async (action:string, method: ("POST"|"GET"), data: { [key: string]: string } = {}, target = "_self")=> {
   const auth = getApi();
   await auth.navigateTo(action, {method: method, target: target, data: data});
}

export const objectToQueryString = (params: { [key: string]: string }): string => {
    return Object.entries(params)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');
}

export const getFormData = (theForm: HTMLFormElement):Record<string, any> =>{
    const formData = new FormData(theForm);
    const jsonData: Record<string, any> = {};

    // Converti FormData in un oggetto JSON
    for (const [key, value] of formData.entries()) {
        jsonData[key] = value;
    }
    
    return jsonData;
}

export const getParameterValue = (param: string) => {
    let location = window.location.href;
    let url = location.slice(location.indexOf('?') + 1).split('&');
    for (let i = 0; i < url.length; i++) {
        let urlparam = url[i].split('=');
        if (urlparam[0] == param) {
            return decodeURI(urlparam[1]);
        }
    }
}

/**
 * Verifica validità del codice di controllo del codice fiscale.
 * Il valore vuoto è "valido" per semplificare la logica di verifica
 * dell'input, assumendo che l'eventuale l'obbligatorietà del campo
 * sia oggetto di un controllo e retroazione distinti.
 * Per aggiornamenti e ulteriori info v. http://www.icosaedro.it/cf-pi
 * @author Umberto Salsi <salsi@icosaedro.it>
 * @version 2016-12-05
 * @param cf Codice fiscale da controllare.
 * @return string Stringa vuota se il codice di controllo è
 * corretto oppure il valore è vuoto, altrimenti un messaggio
 * che descrive perché il valore non può essere valido.
 */
export const checkCodiceFiscale = (cf: string)=>  {
    cf = cf.toUpperCase();
    if (cf == '') return '';
    if (! /^[0-9A-Z]{16}$/.test(cf))
        return "Il codice fiscale deve contenere 16 tra lettere e cifre.";
    var map = [1, 0, 5, 7, 9, 13, 15, 17, 19, 21, 1, 0, 5, 7, 9, 13, 15, 17,
        19, 21, 2, 4, 18, 20, 11, 3, 6, 8, 12, 14, 16, 10, 22, 25, 24, 23];
    var s = 0;
    for (var i = 0; i < 15; i++) {
        var c = cf.charCodeAt(i);
        if (c < 65)
            c = c - 48;
        else
            c = c - 55;
        if (i % 2 == 0)
            s += map[c];
        else
            s += c < 10 ? c : c - 10;
    }
    var atteso = String.fromCharCode(65 + s % 26);
    if (atteso != cf.charAt(15))
        return "Il codice fiscale non è valido:\n" +
            "il codice di controllo non corrisponde.\n";
    return "";
}

export const dateStringToDateUTC = (dateString: string) : Date => {
    // Supporta sia formato italiano (dd/MM/yyyy) che ISO (yyyy-MM-dd)
    const format = dateString.includes('-') ? 'yyyy-MM-dd' : 'dd/MM/yyyy';
    let dt = parse(dateString, format, new Date());
    return new Date(Date.UTC(
        dt.getFullYear(),
        dt.getMonth(),   // 0‑based
        dt.getDate(),    // 1‑based
        0, 0, 0, 0
    ));
}

export { setStaticPlaceholder } from "./setStaticPlaceholder";
export { default as GoogleMapsUtility } from "./GoogleMapsUtility";